import { SearchX } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useFavoriteIds } from "@/hooks/useFavoriteIds";
import { api, mediaUrlFromMap } from "@/lib/api";
import { formatDate, localized } from "@/lib/intl";
import { termKey } from "@/lib/terminology";
import { usePageMeta } from "@/lib/seo";
import { BRAND } from "@/config/brand";

const EventCard = ({ event, media, locale, t, favorited }) => {
  const hero = mediaUrlFromMap(media, event.hero_media_id);
  return (
    <Link
      to={`/e/${event.slug}`}
      data-testid={`catalog-card-${event.slug}`}
      className="group flex flex-col gap-5"
    >
      <div className="relative h-64 lg:h-72 rounded-2xl bg-[#F5F5F3] overflow-hidden">
        {hero ? (
          <img
            src={hero}
            alt={localized(event.title, locale)}
            className="w-full h-full object-cover opacity-95 group-hover:scale-[1.03] transition-transform duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center opacity-25 group-hover:opacity-35 transition-opacity duration-500"
            aria-hidden="true"
            data-testid={`catalog-card-placeholder-${event.slug}`}
          >
            <img src={BRAND.logo} alt="" className="h-8 w-auto grayscale" draggable={false} />
          </div>
        )}
        <Badge className="absolute top-4 left-4 backdrop-blur-xl bg-white/85 text-[#0A0A0A] hover:bg-white/85 border-0">
          {t(termKey(event.lot_count === 1, "count"), { count: event.lot_count })}
        </Badge>
        <FavoriteButton entityType="event" entityId={event.id} initial={favorited} className="absolute top-4 right-4" />
      </div>
      <div className="flex flex-col gap-1.5">
        <h3 className="text-xl font-semibold tracking-[-0.01em] text-[#1d1d1f] leading-snug">
          {localized(event.title, locale)}
        </h3>
        <p className="text-sm text-[#6e6e73]">{t("pub.by", { name: event.organizer_name })}</p>
        <div className="mt-1 flex flex-col gap-0.5 text-xs text-[#6e6e73]">
          {event.starts_at && (
            <span>
              {formatDate(event.starts_at, locale, { dateStyle: "medium" })} — {formatDate(event.ends_at, locale, { dateStyle: "medium" })}
            </span>
          )}
          {event.venue?.city && <span>{[event.venue.city, event.venue.country].filter(Boolean).join(", ")}</span>}
        </div>
      </div>
    </Link>
  );
};

export default function Catalog() {
  const { t, i18n } = useTranslation();
  const [payload, setPayload] = useState(null);
  const [discipline, setDiscipline] = useState("all");
  const favIds = useFavoriteIds();

  usePageMeta({ title: `${t("pub.catalogTitle")} — ${BRAND.name}`, description: t("pub.catalogSubtitle"), localized: true });

  useEffect(() => {
    api
      .get("/public/events")
      .then(({ data }) => setPayload(data))
      .catch(() => setPayload({ events: [], media: {} }));
  }, []);

  const locale = i18n.resolvedLanguage;
  const allDisciplines = payload ? [...new Set(payload.events.flatMap((e) => e.disciplines || []))] : [];
  const events = payload
    ? discipline === "all"
      ? payload.events
      : payload.events.filter((e) => (e.disciplines || []).includes(discipline))
    : [];

  return (
    <div className="max-w-[1024px] mx-auto px-6 py-16 lg:py-24" data-testid="catalog-page">
      <div className="flex flex-col items-center text-center gap-4 mb-10">
        <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.015em] leading-[1.07] text-[#1d1d1f]">
          {t("pub.catalogTitle")}
        </h1>
        <p className="text-lg lg:text-[24px] font-normal leading-snug text-[#6e6e73] max-w-xl">{t("pub.catalogSubtitle")}</p>
      </div>

      {allDisciplines.length > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mb-12" role="tablist" aria-label={t("horse.disciplines")}>
          {["all", ...allDisciplines].map((d) => (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={discipline === d}
              data-testid={`catalog-discipline-${d}`}
              onClick={() => setDiscipline(d)}
              className={`px-4 h-9 rounded-full text-sm font-medium transition-colors duration-200 ${
                discipline === d ? "bg-[#0A0A0A] text-white" : "bg-black/5 text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
              }`}
            >
              {d === "all" ? t("vidcat.all") : t(`disc.${d}`, { defaultValue: d })}
            </button>
          ))}
        </div>
      )}

      {payload === null ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="catalog-skeletons">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-96 rounded-3xl" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <EmptyState
          icon={SearchX}
          testid="catalog-empty-state"
          title={t("pub.emptyCatalogTitle")}
          description={t("pub.emptyCatalogDesc")}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="catalog-grid">
          {events.map((event) => (
            <EventCard key={event.id} event={event} media={payload.media} locale={locale} t={t} favorited={favIds.has(event.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
